import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { OrderAdminResponse } from '@/types/api';

const ORDER_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'cancelled', label: 'Cancelado' },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatDate = (date?: string) => {
  if (!date) return '—';
  try {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;

    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const yyyy = String(parsed.getFullYear());
    const hh = String(parsed.getHours()).padStart(2, '0');
    const min = String(parsed.getMinutes()).padStart(2, '0');

    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch (error) {
    return date;
  }
};

const normalizeSearchForLocal = (value: string) => value.replace(/#/g, '').trim().toLowerCase();

const normalizeDigits = (value?: string | null) => (value ?? '').replace(/\D+/g, '');

const PAGE_SIZE = 15;

const OrdersManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('OrdersManagement deve ser usado dentro de AppProvider');
  }

  const {
    adminOrders,
    loadAdminOrders,
    deleteOrder,
    updateOrderStatus,
  } = context;

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderAdminResponse | null>(null);
  const [page, setPage] = useState(1);

  const skipInitialFetchRef = useRef(true);

  const orders = adminOrders?.data ?? [];

  const statusLookup = useMemo(() =>
    ORDER_STATUSES.reduce<Record<string, string>>((acc, status) => {
      acc[status.value] = status.label;
      return acc;
    }, {}), []);

  const fetchOrders = async () => {
    setIsFetching(true);
    setError(null);
    setFeedback(null);
    try {
      const pagination = await loadAdminOrders({
        page: 1,
        per_page: 500,
      });
      setPage(1);
      if (pagination.data.length === 0) {
        setFeedback('Nenhum pedido encontrado para o filtro selecionado.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar pedidos.';
      setError(message);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!skipInitialFetchRef.current) return;
    skipInitialFetchRef.current = false;

    if (adminOrders && (adminOrders.data?.length ?? 0) > 0) {
      return;
    }

    void fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (orderId: number, status: string) => {
    setIsMutating(true);
    setError(null);
    setFeedback(null);
    try {
      await updateOrderStatus(orderId, status);
      setFeedback('Status do pedido atualizado.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status do pedido.';
      setError(message);
    } finally {
      setIsMutating(false);
    }
  };

  const handleViewDetails = (order: OrderAdminResponse) => {
    setSelectedOrder(order);
  };

  const handleCancel = async (orderId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja cancelar este pedido?');
    if (!confirmation) return;

    setIsMutating(true);
    setError(null);
    setFeedback(null);
    try {
      await deleteOrder(orderId);
      setFeedback('Pedido cancelado com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao cancelar pedido.';
      setError(message);
    } finally {
      setIsMutating(false);
    }
  };

  const closeDetails = () => {
    setSelectedOrder(null);
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchTerm]);

  const filteredOrders = useMemo(() => {
    const termRaw = searchTerm.trim().toLowerCase();
    const term = normalizeSearchForLocal(searchTerm);
    const termDigits = normalizeDigits(termRaw);

    return orders.filter(order => {
      if (statusFilter.trim() && (order.status ?? '') !== statusFilter.trim()) {
        return false;
      }

      if (!termRaw) {
        return true;
      }

      const orderNumber = (order.order_number ?? '').toLowerCase();
      const orderNumberNormalized = normalizeSearchForLocal(orderNumber);
      const matchesOrderNumber = term.length > 0 && orderNumberNormalized.includes(term);

      const customerName = (order.customer_name ?? order.client?.name ?? '').toLowerCase();
      const customerEmail = (order.customer_email ?? order.client?.email ?? '').toLowerCase();

      const customerPhoneDigits = normalizeDigits(order.customer_phone ?? order.client?.phone ?? '');
      const matchesPhone = termDigits.length > 0 && customerPhoneDigits.includes(termDigits);

      const matchesCustomerText =
        (term.length > 0 && customerName.includes(term))
        || (term.length > 0 && customerEmail.includes(term));

      return matchesOrderNumber || matchesCustomerText || matchesPhone;
    });
  }, [orders, searchTerm, statusFilter]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE)), [filteredOrders.length]);

  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, page]);

  const handleChangePage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Pedidos</h2>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => fetchOrders()}
          disabled={isFetching || isMutating}
        >
          Recarregar
        </button>
      </div>

      <div className="admin-filters">
        <div className="admin-filters-row">
          <div className="admin-filters-field">
            <label htmlFor="orders-filter-status">Status</label>
            <select
              id="orders-filter-status"
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
              disabled={false}
            >
              <option value="">Todos</option>
              {ORDER_STATUSES.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-filters-field">
            <label htmlFor="orders-filter-search">Buscar</label>
            <input
              id="orders-filter-search"
              type="text"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Número do pedido ou cliente"
              disabled={false}
            />
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {orders.length === 0 ? (
        <p>{isFetching ? 'Carregando pedidos...' : 'Nenhum pedido encontrado.'}</p>
      ) : (
        <table className="admin-table admin-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Número</th>
              <th>Cliente</th>
              <th>Total</th>
              <th>Status</th>
              <th>Data</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={7}>{isFetching ? 'Buscando pedidos...' : 'Nenhum pedido encontrado.'}</td>
              </tr>
            ) : (
              paginatedOrders.map(order => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.order_number ? `#${order.order_number.replace(/^#/, '')}` : '—'}</td>
                  <td>{order.customer_name ?? order.client?.name ?? '—'}</td>
                  <td>{formatCurrency(order.total_cents)}</td>
                  <td>
                    <span className={`order-status ${order.status ?? 'pending'}`}>
                      {statusLookup[order.status ?? 'pending'] ?? order.status ?? '—'}
                    </span>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td className="actions">
                    <div className="admin-actions">
                      <button
                        type="button"
                        className="icon-button icon-button--details"
                        onClick={() => handleViewDetails(order)}
                        disabled={isFetching || isMutating}
                        title="Ver detalhes do pedido"
                      >
                        🔍
                        <span className="sr-only">Detalhes</span>
                      </button>
                      <button
                        type="button"
                        className="icon-button icon-button--delete"
                        onClick={() => handleCancel(order.id)}
                        disabled={isFetching || isMutating || order.status === 'cancelled' || order.status === 'delivered'}
                        title={
                          order.status === 'cancelled'
                            ? 'Pedido já cancelado'
                            : order.status === 'delivered'
                              ? 'Pedido entregue não pode ser cancelado'
                              : 'Cancelar pedido'
                        }
                      >
                        ❌
                        <span className="sr-only">Cancelar</span>
                      </button>
                      {order.status === 'cancelled' || order.status === 'delivered' ? (
                        <span className={`status-select status-${order.status}`}>
                          {statusLookup[order.status] ?? order.status}
                        </span>
                      ) : (
                        <select
                          className={`status-select status-${order.status ?? 'pending'}`}
                          value={order.status ?? 'pending'}
                          onChange={event => handleStatusChange(order.id, event.target.value)}
                          disabled={isFetching || isMutating}
                        >
                          {ORDER_STATUSES.filter(status => status.value !== 'cancelled').map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="pagination-controls" aria-live="polite">
          <button
            type="button"
            onClick={() => handleChangePage(page - 1)}
            disabled={isFetching || isMutating || page <= 1}
          >
            Anterior
          </button>
          <div className="pagination-info">
            <span>
              Página {page} de {totalPages}
            </span>
            <span className="pagination-total">
              {filteredOrders.length} {filteredOrders.length === 1 ? 'pedido' : 'pedidos'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => handleChangePage(page + 1)}
            disabled={isFetching || isMutating || page >= totalPages}
          >
            Próxima
          </button>
        </div>
      )}

      {selectedOrder && (
        <div className="admin-modal">
          <div className="modal-content order-details-modal">
            <div className="order-details-header">
              <div>
                <h3>Detalhes do Pedido</h3>
                <div className="order-details-subtitle">
                  {selectedOrder.order_number ? `#${selectedOrder.order_number.replace(/^#/, '')}` : `Pedido #${selectedOrder.id}`}
                </div>
              </div>
              <button type="button" className="btn-secondary" onClick={closeDetails}>
                Fechar
              </button>
            </div>

            <div className="order-details-grid">
              <div className="order-details-card">
                <div className="order-details-card-title">Cliente</div>
                <div className="order-details-card-value">
                  {selectedOrder.customer_name ?? selectedOrder.client?.name ?? '—'}
                </div>
                <div className="order-details-card-muted">
                  {selectedOrder.customer_email ?? selectedOrder.client?.email ?? '—'}
                  {' • '}
                  {selectedOrder.customer_phone ?? selectedOrder.client?.phone ?? '—'}
                </div>
              </div>

              <div className="order-details-card">
                <div className="order-details-card-title">Endereço</div>
                <div className="order-details-card-value">
                  {selectedOrder.customer_address
                    ?? (() => {
                      const client = selectedOrder.client;
                      if (!client) return '—';

                      const parts: string[] = [];
                      const streetLine = [client.street, client.number].filter(Boolean).join(', ');
                      if (streetLine) parts.push(streetLine);
                      if (client.complement) parts.push(client.complement);
                      if (client.neighborhood) parts.push(client.neighborhood);

                      const cityLine = [client.city, client.state].filter(Boolean).join('/');
                      if (cityLine) parts.push(cityLine);
                      if (client.zip_code) parts.push(client.zip_code);

                      return parts.length > 0 ? parts.join(' - ') : '—';
                    })()}
                </div>
              </div>

              <div className="order-details-card">
                <div className="order-details-card-title">Resumo</div>
                <div className="order-details-card-row">
                  <span>Total</span>
                  <strong>{formatCurrency(selectedOrder.total_cents)}</strong>
                </div>
                <div className="order-details-card-row">
                  <span>Status</span>
                  <span className={`order-status ${selectedOrder.status ?? 'pending'}`}>
                    {statusLookup[selectedOrder.status ?? 'pending'] ?? selectedOrder.status ?? '—'}
                  </span>
                </div>
                <div className="order-details-card-row">
                  <span>Criado em</span>
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="order-details-section">
              <h4>Itens</h4>
              {selectedOrder.items?.length ? (
                <ul className="order-details-items">
                  {selectedOrder.items.map(item => (
                    <li key={item.id} className="order-details-item">
                      <span className="order-details-item-name">
                        {item.product_name ?? `Produto #${item.product_id}`}
                      </span>
                      <span className="order-details-item-qty">{item.quantity}x</span>
                      <span className="order-details-item-total">{formatCurrency(item.total_cents)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nenhum item cadastrado.</p>
              )}
            </div>

            {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
              <div className="order-details-section">
                <h4>Histórico de status</h4>
                <ul className="order-details-history">
                  {selectedOrder.status_history.map(history => (
                    <li key={history.changed_at} className="order-details-history-item">
                      <span>{formatDate(history.changed_at)}</span>
                      <span>{statusLookup[history.status] ?? history.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
